import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

// Helper: resolve intended post-auth destination.
const resolveRedirect = (location) => {
  const from = location?.state?.from?.pathname;
  if (from && !["/login", "/registro", "/olvide-mi-password", "/reset-password"].includes(from)) {
    const search = location?.state?.from?.search || "";
    return `${from}${search}`;
  }
  return "/dashboard";
};

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido de vuelta");
      navigate(resolveRedirect(location), { replace: true });
    } catch (err) {
      setError(formatApiError(err, "No pudimos iniciar sesión"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Bienvenido de vuelta" subtitle="Accede a tu panel de agente.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">Correo</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
        </div>
        <div>
          <Label htmlFor="password">Contraseña</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12" data-testid="login-submit-btn">
          {loading ? "Ingresando..." : "Iniciar sesión"}
        </Button>
      </form>
      <div className="mt-4 text-center">
        <Link to="/olvide-mi-password" className="text-sm text-[#C8A24A]/80 hover:text-[#C8A24A] hover:underline" data-testid="link-forgot-password">
          ¿Olvidaste tu contraseña?
        </Link>
      </div>
      <p className="mt-6 text-sm text-center text-[#C8A24A]/70">
        ¿No tienes cuenta? <Link to="/registro" className="text-[#C8A24A] font-semibold hover:underline" data-testid="link-to-register">Regístrate</Link>
      </p>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form);
      toast.success("Cuenta creada. ¡A publicar!");
      navigate(resolveRedirect(location), { replace: true });
    } catch (err) {
      setError(formatApiError(err, "No pudimos crear la cuenta"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Crea tu cuenta de agente" subtitle="Publica propiedades y recibe consultas.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="name">Nombre completo</Label>
          <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name" />
        </div>
        <div>
          <Label htmlFor="email">Correo</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email" />
        </div>
        <div>
          <Label htmlFor="phone">Teléfono</Label>
          <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone" />
        </div>
        <div>
          <Label htmlFor="password">Contraseña</Label>
          <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="register-password" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12" data-testid="register-submit-btn">
          {loading ? "Creando..." : "Crear cuenta"}
        </Button>
      </form>
      <p className="mt-6 text-sm text-center text-[#C8A24A]/70">
        ¿Ya tienes cuenta? <Link to="/login" className="text-[#C8A24A] font-semibold hover:underline" data-testid="link-to-login">Inicia sesión</Link>
      </p>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [devToken, setDevToken] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/password-reset/request", { email });
      setSent(true);
      if (data.dev_token) setDevToken(data.dev_token);
      toast.success("Si el correo existe, enviamos instrucciones");
    } catch (err) {
      setError(formatApiError(err, "No pudimos procesar la solicitud"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Recuperar contraseña" subtitle="Te enviaremos instrucciones para restablecerla.">
      {sent ? (
        <div className="space-y-4" data-testid="forgot-sent">
          <div className="rounded-xl border border-[#C8A24A]/30 bg-[#0A0A0A] p-4">
            <p className="text-sm text-[#C8A24A]/90">
              Si el correo existe, recibirás un enlace para restablecer tu contraseña.
            </p>
          </div>
          {devToken && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-2">
              <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold">
                Ambiente de validación
              </p>
              <p className="text-xs text-amber-200/90 leading-relaxed">
                El proveedor de correo aún no está integrado. Copia este token para completar
                el restablecimiento:
              </p>
              <code
                className="block break-all bg-black/40 p-2 rounded text-xs text-amber-100"
                data-testid="forgot-dev-token"
              >
                {devToken}
              </code>
              <Link
                to={`/reset-password?token=${encodeURIComponent(devToken)}`}
                className="inline-block text-xs font-semibold text-[#C8A24A] hover:underline"
                data-testid="link-goto-reset"
              >
                Continuar al reset →
              </Link>
            </div>
          )}
          <Link to="/login" className="block text-center text-sm text-[#C8A24A]/70 hover:text-[#C8A24A]">
            Volver a iniciar sesión
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label htmlFor="email">Correo</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12" data-testid="forgot-submit-btn">
            {loading ? "Enviando..." : "Enviar instrucciones"}
          </Button>
          <Link to="/login" className="block text-center text-sm text-[#C8A24A]/70 hover:text-[#C8A24A] mt-2" data-testid="link-back-to-login">
            Volver a iniciar sesión
          </Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialToken = new URLSearchParams(location.search).get("token") || "";
  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("La contraseña debe tener al menos 6 caracteres");
    if (password !== confirm) return setError("Las contraseñas no coinciden");
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      toast.success("Contraseña actualizada. Inicia sesión.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "No pudimos restablecer la contraseña"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Nueva contraseña" subtitle="Define una contraseña segura para tu cuenta.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label htmlFor="token">Token</Label>
          <Input
            id="token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            required
            data-testid="reset-token"
            placeholder="Pega el token recibido"
          />
        </div>
        <div>
          <Label htmlFor="password">Nueva contraseña</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password" />
        </div>
        <div>
          <Label htmlFor="confirm">Confirmar contraseña</Label>
          <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="reset-confirm" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12" data-testid="reset-submit-btn">
          {loading ? "Actualizando..." : "Actualizar contraseña"}
        </Button>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8" aria-label="ROCAFUERTE — Bienes Raíces">
          <img src="/logo.png" alt="ROCAFUERTE — Bienes Raíces" className="h-20 w-auto rounded-md" />
        </Link>
        <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-8 shadow-sm">
          <h1 className="font-heading text-3xl tracking-tight mb-2">{title}</h1>
          <p className="text-sm text-[#C8A24A]/70 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
